<template>
  <v-form ref="CoparticipationFilterForm" class="fill-width">
    <ExpandableFilterCard
      class="coparticipation-filter"
      elevation="2"
      :loading="loadingPage || loadingTable"
      :search-disabled="!canSearch"
      :auto-search-on-always-visible-change="true"
      :always-visible-values="alwaysVisibleValues"
      :expand-button-hover-text-collapsed="$t('coparticipation.filter.expandSearch')"
      :expand-button-hover-text-expanded="$t('coparticipation.filter.collapseSearch')"
      :search-label="$t('coparticipation.filter.search')"
      :clear-label="$t('coparticipation.filter.clear')"
      @on-search="searchRecords"
      @on-clear="resetFilters"
    >
      <template v-slot:always-visible>
        <div class="coparticipation-filter__fields">
          <PaginatedAutocomplete
            :label="$t('coparticipation.filter.financialGroup')"
            :fetch="fetchFinancialGroups"
            :value="filter.financialGroupId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
            @input="onFinancialGroupChange"
          />

          <PaginatedAutocomplete
            :key="`carrier-${filter.financialGroupId || 'none'}`"
            :label="$t('coparticipation.filter.carrier')"
            :fetch="fetchCarriers"
            :disabled="!filter.financialGroupId"
            :value="filter.carrierId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
            @input="onCarrierChange"
          />

          <PaginatedAutocomplete
            :key="`contract-${filter.financialGroupId || 'none'}`"
            :label="$t('coparticipation.filter.contract')"
            :fetch="fetchContracts"
            :disabled="!filter.financialGroupId"
            :value="filter.contractId"
            item-text="name"
            item-value="id"
            v-bind="autocompleteAttrs"
            @input="onContractChange"
          />

          <div class="coparticipation-filter__competence-menu">
            <v-menu
              ref="competenceMenu"
              v-model="competenceMenuOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="coparticipation-filter__competence-field"
                  :value="competenceLabel"
                  :label="$t('coparticipation.filter.competence')"
                  :placeholder="$t('coparticipation.filter.placeholders.competence')"
                  readonly
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearCompetence"
                >
                  <template v-slot:append>
                    <v-icon size="20" color="primary">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="competenceDate"
                type="month"
                no-title
                color="primary"
                scrollable
                @input="onCompetencePickerInput"
              >
                <v-spacer />
                <v-btn
                  text
                  color="primary"
                  @click="competenceMenuOpen = false"
                >
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="confirmCompetence"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </div>
        </div>
      </template>

      <template v-slot:expanded>
        <div class="coparticipation-filter__expanded">
          <v-text-field
            :label="$t('coparticipation.filter.beneficiary')"
            :placeholder="$t('coparticipation.filter.placeholders.beneficiary')"
            :value="filter.beneficiary"
            outlined
            hide-details
            clearable
            @input="onBeneficiaryChange"
          />

          <v-select
            :label="$t('coparticipation.filter.copayExempt')"
            :items="copayExemptOptions"
            :value="copayExemptSelectValue"
            item-text="text"
            item-value="value"
            outlined
            hide-details
            :placeholder="$t('coparticipation.filter.placeholders.select')"
            @input="onCopayExemptChange"
          />
        </div>
      </template>
    </ExpandableFilterCard>
  </v-form>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import ExpandableFilterCard from '@/components/Default/ExpandableFilterCard.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import CoparticipationService from '@/services-http/servicos/CoparticipationService';
import {
  COPAY_EXEMPT_OPTIONS,
  mapCopayExemptFilterToOption,
  mapCopayExemptOptionToFilter,
} from '@/store/modules/coparticipation/coparticipation-config';

export default {
  name: 'CoparticipationFilter',
  components: { ExpandableFilterCard, PaginatedAutocomplete },
  data: () => ({
    competenceMenuOpen: false,
    competenceDate: null,
    filterService: null,
  }),
  computed: {
    ...mapGetters({
      filter: 'coparticipation/filter',
      loadingPage: 'coparticipation/loadingPage',
      loadingTable: 'coparticipation/loadingTable',
      canSearch: 'coparticipation/canSearch',
    }),
    competenceLabel() {
      if (!this.filter.competence) return '';
      const parsed = moment(this.filter.competence, ['YYYY-MM', 'YYYY-MM-DD'], true);
      if (!parsed.isValid()) return this.filter.competence;
      return parsed.format('MM/YYYY');
    },
    copayExemptOptions() {
      return COPAY_EXEMPT_OPTIONS.map((option) => ({
        value: option.value,
        text: this.$t(`coparticipation.filter.copayExemptOptions.${option.i18nKey}`),
      }));
    },
    copayExemptSelectValue() {
      return mapCopayExemptFilterToOption(this.filter.copayExempt);
    },
    alwaysVisibleValues() {
      return {
        financialGroupId: this.filter.financialGroupId,
        carrierId: this.filter.carrierId,
        contractId: this.filter.contractId,
        competence: this.filter.competence,
      };
    },
    autocompleteAttrs() {
      return {
        clearable: true,
        regular: true,
        color: 'primary',
        itemColor: 'primary',
        placeholder: this.$t('coparticipation.filter.placeholders.select'),
        persistentPlaceholder: false,
        borderSelected: true,
        hideDetails: true,
        groupByHeader: false,
        minSearchChars: 0,
      };
    },
  },
  watch: {
    'filter.competence': {
      immediate: true,
      handler(competence) {
        this.competenceDate = competence || null;
      },
    },
  },
  methods: {
    ...mapActions({
      applyFilter: 'coparticipation/applyFilter',
      resetFilter: 'coparticipation/resetFilter',
      searchByFilter: 'coparticipation/searchByFilter',
    }),
    onFinancialGroupChange(financialGroupId) {
      this.applyFilter({
        financialGroupId: financialGroupId || null,
        carrierId: null,
        contractId: null,
      });
    },
    onCarrierChange(carrierId) {
      this.applyFilter({ carrierId: carrierId || null });
    },
    onContractChange(contractId) {
      this.applyFilter({ contractId: contractId || null });
    },
    onBeneficiaryChange(beneficiary) {
      this.applyFilter({ beneficiary: beneficiary || null });
    },
    onCopayExemptChange(copayExempt) {
      this.applyFilter({ copayExempt: mapCopayExemptOptionToFilter(copayExempt) });
    },
    onCompetencePickerInput(value) {
      this.competenceDate = value;
    },
    confirmCompetence() {
      this.competenceMenuOpen = false;
      if (!this.competenceDate) return;
      this.applyFilter({ competence: this.competenceDate });
    },
    clearCompetence() {
      this.competenceDate = null;
      this.applyFilter({ competence: null });
    },
    async fetchFinancialGroups(page, size, searchText) {
      return this.filterService.fetchFinancialGroups(page, size, searchText);
    },
    async fetchCarriers(page, size, searchText) {
      return this.filterService.fetchCarriers(
        page,
        size,
        searchText,
        this.filter.financialGroupId,
      );
    },
    async fetchContracts(page, size, searchText) {
      return this.filterService.fetchContracts(
        page,
        size,
        searchText,
        this.filter.financialGroupId,
      );
    },
    async searchRecords() {
      if (!this.canSearch) return;
      await this.searchByFilter();
    },
    async resetFilters() {
      await this.resetFilter();
      this.competenceDate = null;
    },
  },
  created() {
    this.filterService = new CoparticipationService();
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/coparticipation/filter.scss";
</style>
